import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';

import { useApiClient } from '@/api/clients/useApiClient.util.ts';
import { TasksPageableResponseDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { CustomTableRequestModel } from '@/models/CustomTableRequest.model.ts';
import { TasksListSearchFormModel } from '@/views/tasks/list/model/TasksListSearchForm.model.ts';

type Req = CustomTableRequestModel<TasksListSearchFormModel>;

type Return = {
  getTasks: (req: Req) => Promise<TasksPageableResponseDto>;
  isLoading: boolean;
};

export const formatTaskSearchDate = (date: dayjs.Dayjs | null | undefined) => {
  if (!date) {
    return undefined;
  }

  return date.format('YYYY-MM-DD') + 'T' + date.format('HH:mm:ss.SSS') + 'Z';
};

export const useGetTasks = (): Return => {
  const { tasksApi } = useApiClient();

  const { isPending, mutateAsync } = useMutation<TasksPageableResponseDto, AxiosError, Req>({
    meta: { disableSuccessAlert: true },
    mutationFn: async req => {
      const res = await tasksApi.searchTasks(req.page, req.pageSize, {
        ...req.params,
        assignedUserId: req.params.assignedUserId?.value,
        contextResourceId: undefined,
        contextResourceTypes: req.params.contextResourceTypes
          ?.filter(it => Boolean(it.value))
          .map(it => it.value),
        creationDateFrom: formatTaskSearchDate(req.params.creationDateFrom),
        creationDateTo: formatTaskSearchDate(req.params.creationDateTo),
        creatorUserId: req.params.creatorUserId?.value,
        dueDateFrom: formatTaskSearchDate(req.params.dueDateFrom),
        dueDateTo: formatTaskSearchDate(req.params.dueDateTo),
        taskStatuses: req.params.taskStatuses?.filter(it => Boolean(it.value)).map(it => it.value),
        watchersUserIds: req.params.watchersUserIds
          ?.filter(it => Boolean(it?.value))
          .map(it => it.value),
      });

      return res.data;
    },
    mutationKey: [QueryKeyEnum.TASKS.ALL],
  });

  return {
    getTasks: mutateAsync,
    isLoading: isPending,
  };
};
